/**
 * SidebarStateManager - Manages the state of multiple sidebars ensuring only one is visible at a time
 */
import NavigationManager from './NavigationManager';
import { isBrowser } from './isServer';

export enum SidebarType {
  RESTAURANT_DETAILS = 'restaurant_details',
  RECENT_REPORTS = 'recent_reports',
  TRENDS = 'trends',
  CITY_STATS = 'city_stats',
  PROVINCE_STATS = 'province_stats',
  CHAIN_DETAILS = 'chain_details',
  CHAIN_RANKINGS = 'chain_rankings',
  BACKLOG = 'backlog'
}

export interface SidebarState {
  activeSidebar: SidebarType | null;
  previousSidebar: SidebarType | null;
}

export class SidebarStateManager {
  private state: SidebarState = {
    activeSidebar: null,
    previousSidebar: null
  };

  private listeners: Array<(state: SidebarState) => void> = [];
  private navigationManager: NavigationManager | null = null;

  constructor() {
    if (isBrowser) {
      this.navigationManager = NavigationManager.getInstance();
    }
  }

  /**
   * Subscribe to sidebar state changes
   */
  subscribe(listener: (state: SidebarState) => void): () => void {
    this.listeners.push(listener);
    
    // Return unsubscribe function
    return () => {
      const index = this.listeners.indexOf(listener);
      if (index > -1) {
        this.listeners.splice(index, 1);
      }
    };
  }

  /**
   * Notify all listeners of state changes
   */
  private notifyListeners(): void {
    this.listeners.forEach(listener => listener({ ...this.state }));
  }

  /**
   * Open a specific sidebar, closing any currently open sidebar
   */
  openSidebar(sidebarType: SidebarType): void {
    if (this.state.activeSidebar === sidebarType) {
      return; // Already open
    }

    this.state.previousSidebar = this.state.activeSidebar;
    this.state.activeSidebar = sidebarType;
    
    // Register with NavigationManager for back button handling
    this.navigationManager?.registerOverlay(
      'sidebar',
      sidebarType,
      () => this.closeSidebar()
    );
    
    this.notifyListeners();
  }

  /**
   * Close the currently active sidebar
   */
  closeSidebar(): void {
    if (this.state.activeSidebar === null) {
      return; // Already closed
    }

    // Unregister from NavigationManager
    this.navigationManager?.unregisterOverlay(this.state.activeSidebar);
    
    this.state.previousSidebar = this.state.activeSidebar;
    this.state.activeSidebar = null;
    this.notifyListeners();
  }

  /**
   * Close a specific sidebar (only if it's currently active)
   */
  closeSpecificSidebar(sidebarType: SidebarType): void {
    if (this.state.activeSidebar === sidebarType) {
      this.closeSidebar();
    }
  }

  /**
   * Check if a specific sidebar is currently active
   */
  isSidebarActive(sidebarType: SidebarType): boolean {
    return this.state.activeSidebar === sidebarType;
  }

  /**
   * Get the current sidebar state
   */
  getState(): SidebarState {
    return { ...this.state };
  }

  /**
   * Get the currently active sidebar type
   */
  getActiveSidebar(): SidebarType | null {
    return this.state.activeSidebar;
  }

  /**
   * Toggle a sidebar - open if closed, close if open
   */
  toggleSidebar(sidebarType: SidebarType): void {
    if (this.state.activeSidebar === sidebarType) {
      this.closeSidebar();
    } else {
      this.openSidebar(sidebarType);
    }
  }

  /**
   * Close all sidebars - used for global deselection
   */
  closeAllSidebars(): void {
    if (this.state.activeSidebar !== null) {
      this.closeSidebar();
    }
  }

  /**
   * Check if any sidebar is currently active
   */
  hasActiveSidebar(): boolean {
    return this.state.activeSidebar !== null;
  }
}

// Create a singleton instance
export const sidebarStateManager = new SidebarStateManager();

/**
 * Derive which sidebar should be open for a given URL/initialData payload.
 * Used by entry-server.tsx (before renderToString) and entry-client.tsx
 * (before hydrateRoot) so the singleton state matches across SSR and the
 * first client render — preventing hydration mismatches on routes that
 * auto-open a sidebar (restaurant, city, province, trending, recent-reports).
 */
export function determineActiveSidebar(
  url: string,
  initialData?: { restaurant?: any; cityStats?: any; provinceStats?: any; chainStats?: any }
): SidebarType | null {
  if (initialData?.restaurant) return SidebarType.RESTAURANT_DETAILS;
  if (initialData?.cityStats) return SidebarType.CITY_STATS;
  if (initialData?.provinceStats) return SidebarType.PROVINCE_STATS;
  if (initialData?.chainStats) return SidebarType.CHAIN_DETAILS;
  if (/\/recent-reports(\/|$)/.test(url)) return SidebarType.RECENT_REPORTS;
  if (/\/trending(\/|$)/.test(url)) return SidebarType.TRENDS;
  // Rankings is URL-based (like trending); chain detail is data-based above
  if (/\/(?:ketens|chaines|chains)(\/|$|\?)/.test(url)) return SidebarType.CHAIN_RANKINGS;
  if (/\/(?:inspectie-achterstand|retard-inspection|inspection-backlog)(\/|$|\?)/.test(url)) return SidebarType.BACKLOG;
  return null;
}